import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Button from "react-bootstrap/Button";
import { DataGrid } from "@mui/x-data-grid";

import { API_URL } from "../components/api";
import { formatDateTime } from "../../utils/dateFormat";
import "./Internet.css";


const dateColumn = (field, headerName, minWidth = 180) => ({
  field,
  headerName,
  minWidth,
  flex: 1,
  valueFormatter: (params) => (
    params.value ? formatDateTime(params.value) : "-"
  ),
});


function TestingClients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actingId, setActingId] = useState(null);
  const [pageSize, setPageSize] = useState(25);

  const authTokens = JSON.parse(
    localStorage.getItem("authTokens") || "{}",
  );
  const authorization = `Bearer ${authTokens.access}`;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_URL}/radius/testing-clients/`,
        {
          headers: {
            Authorization: authorization,
          },
        },
      );

      setRows(response.data?.results || []);
    } catch (requestError) {
      setRows([]);
      setError(
        requestError.response?.data?.error
        || "Unable to load Testing Clients.",
      );
    } finally {
      setLoading(false);
    }
  }, [authorization]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccess("");
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [success]);

  const resolveRequest = async (requestId, action) => {
    if (actingId !== null) return;

    setActingId(requestId);
    setError("");
    setSuccess("");

    try {
      await axios.post(
        `${API_URL}/radius/testing-clients/`,
        {
          action,
          request_id: requestId,
        },
        {
          headers: {
            Authorization: authorization,
          },
        },
      );

      await fetchRows();
      setSuccess(
        action === "accept"
          ? "Testing request accepted successfully."
          : "Testing request rejected successfully.",
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.error
        || `Unable to ${action} the testing request.`,
      );
    } finally {
      setActingId(null);
    }
  };

  const columns = [
    {
      field: "client_name",
      headerName: "Client",
      minWidth: 210,
      flex: 1.3,
    },
    {
      field: "subscription_username",
      headerName: "Subscription",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "original_plan_name",
      headerName: "Original Plan",
      minWidth: 140,
      flex: 0.8,
    },
    {
      field: "tested_plan_name",
      headerName: "Tested Plan",
      minWidth: 140,
      flex: 0.8,
    },
    dateColumn("checked_at", "Checked At"),
    dateColumn("expires_at", "Testing Expiry"),
    {
      field: "requested_by",
      headerName: "Requested By",
      minWidth: 130,
      flex: 0.8,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 210,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="testing-client-actions">
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={actingId !== null}
            onClick={() => (
              resolveRequest(params.row.request_id, "reject")
            )}
          >
            {actingId === params.row.request_id
              ? "Working..."
              : "Reject"}
          </Button>

          <Button
            type="button"
            variant="success"
            size="sm"
            disabled={actingId !== null}
            onClick={() => (
              resolveRequest(params.row.request_id, "accept")
            )}
          >
            {actingId === params.row.request_id
              ? "Working..."
              : "Accept"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <main
      className={
        "testing-clients-content testing-clients-page"
      }
    >
          <div className="account-heading">
            <button
              type="button"
              className="edit-client"
              onClick={fetchRows}
              disabled={loading || actingId !== null}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {success && (
            <div
              className="testing-clients-success"
              role="status"
              aria-live="polite"
            >
              {success}
            </div>
          )}

          {error && (
            <div className="testing-clients-error">
              {error}
            </div>
          )}

          <div className="testing-clients-grid">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.request_id}
              getRowHeight={() => "auto"}
              getRowClassName={(params) => (
                params.row.urgent
                  ? "testing-client-urgent"
                  : ""
              )}
              loading={loading}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              rowsPerPageOptions={[10, 25, 50, 100]}
              pagination
              disableSelectionOnClick
            />
          </div>
    </main>
  );
}


export default TestingClients;
